import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { User, Lock, ArrowRight, AlertCircle, Key, X, Eye, EyeOff } from 'lucide-react'
import { useStore } from '../../store/useStore'
import AuthService from '../../services/authService'
import GlassCard from '../ui/GlassCard'
import Button from '../ui/Button'
import Input from '../ui/Input'

const DirectLoginForm = () => {
  const navigate = useNavigate()
  const { login } = useStore()
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [resetLoading, setResetLoading] = useState(false)
  const [resetError, setResetError] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [resetSuccess, setResetSuccess] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      // Utiliser AuthService pour la validation
      const isValid = await AuthService.validateCredentials(formData.email, formData.password)
      
      if (isValid) {
        // Récupérer les données utilisateur depuis AuthService
        const adminCredentials = AuthService.validCredentials[formData.email]
        
        // Créer la session
        const sessionData = {
          id: `admin-${Date.now()}`,
          email: formData.email,
          name: adminCredentials.name,
          role: adminCredentials.role,
          permissions: adminCredentials.permissions,
          loginTime: Date.now(),
          sessionId: 'direct-session-' + Date.now(),
          lastActivity: Date.now()
        }

        // Stocker dans localStorage
        localStorage.setItem('admin_session_simple', JSON.stringify(sessionData))
        
        // Mettre à jour le store Zustand
        login(sessionData)
        
        console.log('✅ Session créée et store mis à jour:', sessionData)
        
        // Redirection
        setTimeout(() => {
          window.location.href = '/admin'
        }, 200)
      } else {
        setError('Email ou mot de passe incorrect')
        setLoading(false)
      }
    } catch (error) {
      console.error('Erreur lors de la connexion:', error)
      setError('Erreur lors de la connexion. Veuillez réessayer.')
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
    setError('')
  }

  const handleForgotPassword = async (e) => {
    e.preventDefault()
    setResetLoading(true)
    setResetError('')

    if (newPassword !== confirmPassword) {
      setResetError('Les mots de passe ne correspondent pas')
      setResetLoading(false)
      return
    }

    if (newPassword.length < 6) {
      setResetError('Le mot de passe doit contenir au moins 6 caractères')
      setResetLoading(false)
      return
    }

    try {
      await AuthService.resetForgottenPassword(newPassword)
      setResetSuccess(true)
      setNewPassword('')
      setConfirmPassword('')
      
      // Fermer le modal après 3 secondes
      setTimeout(() => {
        setShowForgotPassword(false)
        setResetSuccess(false)
      }, 3000)
    } catch (error) {
      setResetError(error.message)
    } finally {
      setResetLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-green-50 via-white to-blue-50">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <GlassCard className="p-8">
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <User className="w-8 h-8 text-primary-600" />
            </motion.div>
            
            <h1 className="text-2xl font-display font-bold gradient-text mb-2">
              Connexion Admin
            </h1>
            
            <p className="text-gray-600">
              Résidences Masséna
            </p>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3"
            >
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <p className="text-red-800 text-sm">{error}</p>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Entrez votre email"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mot de passe
              </label>
              <input
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Entrez votre mot de passe"
                required
              />
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={loading}
              className="w-full"
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </Button>

            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-primary-600 hover:text-primary-700 underline"
              >
                Mot de passe oublié ?
              </button>
            </div>
          </form>
        </GlassCard>
      </motion.div>

      {/* Modal Mot de passe oublié */}
      <AnimatePresence>
        {showForgotPassword && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              className="w-full max-w-md"
            >
              <GlassCard className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                      <Key className="w-5 h-5 text-primary-600" />
                    </div>
                    <h2 className="text-xl font-bold text-black">
                      Réinitialiser le mot de passe
                    </h2>
                  </div>
                  <button
                    onClick={() => {
                      setShowForgotPassword(false)
                      setResetError('')
                      setResetSuccess(false)
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {resetSuccess ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-6"
                  >
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Key className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-green-800 mb-2">
                      Mot de passe réinitialisé !
                    </h3>
                    <p className="text-green-600 text-sm">
                      Votre nouveau mot de passe a été défini avec succès.
                      Vous pouvez maintenant vous connecter.
                    </p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-800">
                        <strong>Important :</strong> Cette action réinitialisera le mot de passe de l'administrateur principal.
                        Vous devrez utiliser le nouveau mot de passe pour vous connecter.
                      </p>
                    </div>

                    {resetError && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
                        <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                        <p className="text-red-800 text-sm">{resetError}</p>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nouveau mot de passe
                      </label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Minimum 6 caractères"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Confirmer le nouveau mot de passe
                      </label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Répétez le mot de passe"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
                      />
                    </div>

                    <div className="flex space-x-3 pt-4">
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => {
                          setShowForgotPassword(false)
                          setResetError('')
                          setNewPassword('')
                          setConfirmPassword('')
                        }}
                        className="flex-1"
                      >
                        Annuler
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        loading={resetLoading}
                        className="flex-1"
                        icon={<Key className="w-4 h-4" />}
                      >
                        {resetLoading ? 'Réinitialisation...' : 'Réinitialiser'}
                      </Button>
                    </div>
                  </form>
                )}
              </GlassCard>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default DirectLoginForm
